/**
 * React Query hooks for properties
 * Includes query key factory for granular cache invalidation
 */
import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useCallback } from 'react';
import { propertiesApi } from './properties.api';
import type { Property, PropertyFilters, PropertiesResponse, District, LocalizedField } from '@/types';

/**
 * Query key factory
 * Prevents key collisions and enables granular invalidation
 */
export const propertyKeys = {
  all: ['properties'] as const,
  lists: () => [...propertyKeys.all, 'list'] as const,
  list: (filters: PropertyFilters) => [...propertyKeys.lists(), filters] as const,
  details: () => [...propertyKeys.all, 'detail'] as const,
  detail: (id: string) => [...propertyKeys.details(), id] as const,
  districts: (city?: string) => [...propertyKeys.all, 'districts', city] as const,
  cities: () => [...propertyKeys.all, 'cities'] as const,
  types: () => [...propertyKeys.all, 'types'] as const,
};

/**
 * Stale times for different query types
 */
const STALE_TIMES = {
  list: 5 * 60 * 1000, // 5 minutes
  detail: 10 * 60 * 1000, // 10 minutes
  filters: 30 * 60 * 1000, // 30 minutes (districts, types, cities change rarely)
} as const;

type QueryOptions<TData> = Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>;

/**
 * Hook to get properties list with filters
 */
export function useProperties(
  filters: PropertyFilters = {},
  options: QueryOptions<PropertiesResponse> = {}
) {
  return useQuery({
    queryKey: propertyKeys.list(filters),
    queryFn: () => propertiesApi.getProperties(filters),
    staleTime: STALE_TIMES.list,
    ...options,
  });
}

/**
 * Hook to get a single property by ID
 */
export function useProperty(
  id: string | null | undefined,
  options: QueryOptions<Property | null> = {}
) {
  return useQuery({
    queryKey: propertyKeys.detail(id || ''),
    queryFn: () => propertiesApi.getPropertyById(id!),
    enabled: Boolean(id),
    staleTime: STALE_TIMES.detail,
    ...options,
  });
}

/**
 * Hook to get districts for filter dropdown
 * Returns districts with listing counts
 */
export function useDistricts(
  city?: string,
  options: QueryOptions<District[]> = {}
) {
  return useQuery({
    queryKey: propertyKeys.districts(city),
    queryFn: () => propertiesApi.getDistricts(city),
    staleTime: STALE_TIMES.filters,
    ...options,
  });
}

/**
 * Hook to get cities for filter dropdown
 */
export function useCities(options: QueryOptions<LocalizedField[]> = {}) {
  return useQuery({
    queryKey: propertyKeys.cities(),
    queryFn: () => propertiesApi.getCities(),
    staleTime: STALE_TIMES.filters,
    ...options,
  });
}

/**
 * Hook to get property types for filter dropdown
 */
export function usePropertyTypes(options: QueryOptions<LocalizedField[]> = {}) {
  return useQuery({
    queryKey: propertyKeys.types(),
    queryFn: () => propertiesApi.getPropertyTypes(),
    staleTime: STALE_TIMES.filters,
    ...options,
  });
}

/**
 * Hook to prefetch a property on hover/focus
 * Returns a function to call on mouseEnter/focus
 */
export function usePrefetchProperty() {
  const queryClient = useQueryClient();

  return useCallback(
    (id: string) => {
      queryClient.prefetchQuery({
        queryKey: propertyKeys.detail(id),
        queryFn: () => propertiesApi.getPropertyById(id),
        staleTime: STALE_TIMES.detail,
      });
    },
    [queryClient]
  );
}

interface InvalidateOptions {
  type?: 'all' | 'list' | 'detail';
  id?: string;
}

/**
 * Hook to invalidate properties cache
 * Useful after mutations
 */
export function useInvalidateProperties() {
  const queryClient = useQueryClient();

  return useCallback(
    (options: InvalidateOptions = {}) => {
      const { type = 'all' } = options;

      switch (type) {
        case 'list':
          queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
          break;
        case 'detail':
          if (options.id) {
            queryClient.invalidateQueries({
              queryKey: propertyKeys.detail(options.id),
            });
          } else {
            queryClient.invalidateQueries({ queryKey: propertyKeys.details() });
          }
          break;
        case 'all':
        default:
          queryClient.invalidateQueries({ queryKey: propertyKeys.all });
      }
    },
    [queryClient]
  );
}
